
import { useState, useRef, useEffect } from "react";
import {
  Send,
  Phone,
  Instagram,
  Facebook,
  Youtube,
  Music,
  Info,
} from "lucide-react";

export default function ChatBot() {
  const [messages, setMessages] = useState([
    {
      from: "bot",
      text: "أهلاً بيك في شركة ZED Delivery 👋🚚\nأسرع شركة توصيل في الإسماعيلية 💙\nتحب أساعدك في إيه النهارده؟",
      time: new Date().toLocaleTimeString("ar-EG", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    },
  ]);

  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [conversationState, setConversationState] = useState("initial");
  const messagesEndRef = useRef(null);

  // 🔹 بيانات التواصل
  const contactInfo = {
    numbers: [
      { label: "01225009497" },
      { label: "01015758759" },
      { label: "01285433443" },
    ],
    support: "01093888948",
    manager: "01284845513",
  };

  // 🔹 روابط السوشيال ميديا
  const socialLinks = {
    youtube: "https://www.youtube.com/@zeddelivery1?si=jk6oBzFSkZ-JtGcQ",
    instagram: "https://www.instagram.com/zed.ismailia/",
    tiktok: "https://www.tiktok.com/@zeddelivery1",
    facebook: "https://www.facebook.com/people/Zed-Delivery/100088900852558/",
  };

  const scrollToBottom = () =>
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const addMessage = (from, text) => {
    setMessages((prev) => [
      ...prev,
      {
        from,
        text,
        time: new Date().toLocaleTimeString("ar-EG", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      },
    ]);
  };

  const simulateTyping = (response, state = "initial") => {
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      addMessage("bot", response);
      setConversationState(state);
    }, 800 + Math.random() * 400);
  };

  const handleConversation = (userInput) => {
    const input = userInput.trim();
    addMessage("user", input);

    if (input.includes("طلب") || input.includes("أوردر")) {
      simulateTyping(
        "علشان تطلب أوردر 🚚 كل اللي عليك تكلمنا على الأرقام أو تبعتلنا على أي صفحة من صفحاتنا وهيوصلك المندوب لحد باب بيتك 💨"
      );
    } else if (
      input.includes("zed") ||
      input.includes("الشركة") ||
      input.includes("مين") ||
      input.includes("معلومات")
    ) {
      simulateTyping("تمام ✅ دي معلومات عن شركة ZED Delivery 💙👇", "info");
    } else if (
      input.includes("تواصل") ||
      input.includes("دعم") ||
      input.includes("واتساب") ||
      input.includes("رقم")
    ) {
      simulateTyping(
        "تمام ✅ دي كل طرق التواصل الرسمية مع شركة ZED 👇",
        "support"
      );
    } else {
      simulateTyping(
        "أنا أقدر أساعدك في 👇\n• طريقة طلب الأوردر 🚚\n• معلومات عن شركة ZED Delivery 🏢\n• التواصل مع الدعم الفني 💬"
      );
    }
  };

  const handleSend = () => {
    if (inputValue.trim()) {
      handleConversation(inputValue);
      setInputValue("");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const quickOptions = [
    { text: "طريقة طلب الأوردر", icon: "🚚" },
    { text: "معلومات عن ZED Delivery", icon: "💙" },
    { text: "التواصل مع الدعم", icon: "💬" },
  ];

  return (
    <div className="flex justify-center items-center h-full p-4">
      <div className="w-full h-full bg-white flex flex-col overflow-hidden rounded-3xl shadow-xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-2.5 flex items-center gap-2">
          <div className="w-8 h-8 bg-blue-950 rounded-full flex items-center justify-center shadow-md overflow-hidden">
            <img
              src="logo512.png"
              alt="ZED Logo"
              className="w-6 h-6 object-contain"
            />
          </div>
          <div>
            <h2 className="font-bold text-sm">مساعد ZED Delivery</h2>
            <p className="text-[10px] text-blue-100 flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span>{" "}
              متاح الآن
            </p>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 p-4 overflow-y-auto bg-gray-50 space-y-4">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex ${
                msg.from === "user" ? "justify-end" : "justify-start"
              } animate-fade-in`}
            >
              <div
                className={`flex flex-col ${
                  msg.from === "user" ? "items-end" : "items-start"
                } max-w-[80%]`}
              >
                <div
                  className={`px-4 py-3 rounded-2xl shadow-md whitespace-pre-line ${
                    msg.from === "user"
                      ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-br-none"
                      : "bg-white text-gray-800 rounded-bl-none border border-gray-100"
                  }`}
                >
                  {msg.text}

                  {/* 🔹 بلوك الدعم */}
                  {conversationState === "support" && msg.from === "bot" && (
                    <div className="mt-3 space-y-2 text-sm animate-fade-in">
                      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-3">
                        <h3 className="font-bold text-blue-700 mb-1">
                          📞 أرقام التواصل:
                        </h3>
                        {contactInfo.numbers.map((n, index) => (
                          <p key={index}>
                            <a
                              href={`tel:${n.label}`}
                              className="text-blue-600 hover:underline"
                            >
                              {n.label}
                            </a>
                          </p>
                        ))}
                      </div>
                      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-3">
                        <h3 className="font-bold text-red-600 mb-1">
                          🚨 رقم الشكاوى:
                        </h3>
                        <a
                          href={`tel:${contactInfo.support}`}
                          className="text-red-600 hover:underline"
                        >
                          {contactInfo.support}
                        </a>
                      </div>
                      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-3">
                        <h3 className="font-bold text-purple-600 mb-1">
                          👨‍💼 مدير الشركة:
                        </h3>
                        <a
                          href={`tel:${contactInfo.manager}`}
                          className="text-purple-600 hover:underline"
                        >
                          {contactInfo.manager}
                        </a>
                      </div>
                    </div>
                  )}

                  {/* 🔹 بلوك معلومات عن ZED */}
                  {conversationState === "info" && msg.from === "bot" && (
                    <div className="mt-3 space-y-3 text-sm animate-fade-in">
                      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-3">
                        <h3 className="font-bold text-blue-700 mb-1 flex items-center gap-1">
                          <Info size={16} /> عن الشركة:
                        </h3>
                        <p>
                          ZED Delivery شركة توصيل دليفري في محافظة الإسماعيلية
                          🚚💙
                          <br /> بنخدم كل المناطق وبنوصلك أسرع من أي حد ⚡
                          <br /> هدفنا راحتك وتوصيل أوردراتك في أسرع وقت وبأفضل
                          سعر!
                        </p>
                      </div>
                      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-3">
                        <h3 className="font-bold text-blue-700 mb-1">
                          📲 تابعنا على السوشيال:
                        </h3>
                        <div className="flex flex-wrap gap-2 justify-start">
                          <a
                            href={socialLinks.facebook}
                            target="_blank"
                            rel="noreferrer"
                            className="bg-blue-600 text-white px-3 py-1 rounded-full flex items-center gap-1 hover:bg-blue-700"
                          >
                            <Facebook size={16} /> فيسبوك
                          </a>
                          <a
                            href={socialLinks.instagram}
                            target="_blank"
                            rel="noreferrer"
                            className="bg-pink-500 text-white px-3 py-1 rounded-full flex items-center gap-1 hover:bg-pink-600"
                          >
                            <Instagram size={16} /> إنستجرام
                          </a>
                          <a
                            href={socialLinks.tiktok}
                            target="_blank"
                            rel="noreferrer"
                            className="bg-black text-white px-3 py-1 rounded-full flex items-center gap-1 hover:bg-gray-800"
                          >
                            <Music size={16} /> تيك توك
                          </a>
                          <a
                            href={socialLinks.youtube}
                            target="_blank"
                            rel="noreferrer"
                            className="bg-red-600 text-white px-3 py-1 rounded-full flex items-center gap-1 hover:bg-red-700"
                          >
                            <Youtube size={16} /> يوتيوب
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <span className="text-xs text-gray-400 mt-1 px-1">
                  {msg.time}
                </span>
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="flex justify-start animate-fade-in">
              <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-none shadow-md border border-gray-100">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200"></span>
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-400"></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* أزرار الخيارات السريعة */}
        <div className="px-2 py-2 bg-white border-t border-gray-100 animate-pop-up">
          <p className="text-[10px] text-gray-500 mb-1.5 text-center">
            اختر من القايمة دي 👇
          </p>
          <div className="flex flex-wrap gap-1.5 justify-center">
            {quickOptions.map((option, i) => (
              <button
                key={i}
                onClick={() => handleConversation(option.text)}
                className="flex items-center gap-1.5 bg-gradient-to-r from-blue-100 via-blue-50 to-indigo-100 hover:from-blue-200 hover:to-indigo-200 text-blue-700 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all shadow-sm hover:shadow-md border border-blue-200"
              >
                <span className="text-sm">{option.icon}</span>
                <span>{option.text}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Input */}
        <div className="p-4 bg-white border-t border-gray-100 rounded-b-3xl">
          <div className="flex gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="اكتب رسالتك هنا..."
              className="flex-1 px-4 py-3 rounded-full border-2 border-gray-200 focus:border-blue-500 focus:outline-none text-right bg-gray-50 transition-all"
              dir="rtl"
            />
            <button
              onClick={handleSend}
              disabled={!inputValue.trim()}
              className="w-14 h-14 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-5 rounded-full hover:from-blue-700 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pop-up {
          0% { opacity: 0; transform: scale(0.9) translateY(20px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.3s ease-out; }
        .animate-pop-up { animation: pop-up 0.4s ease-out; }
      `}</style>
    </div>
  );
}
